import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { AgentWizardData } from '@/schemas/agent-wizard'
import { useProjectStore } from '@/stores/project-store'
import { Search, Brain, Check, FileText, Database, AlertCircle } from 'lucide-react'
import { useDebounce } from '@/hooks/use-debounce'
import api from '@/services/api'

// Knowledge base interface (from backend)
interface KnowledgeBase {
  id: string
  name: string
  description: string | null
  document_count: number
  chunk_count?: number // Optional - may not be present in API response
  embedding_model?: string | null // Optional - may be in chunking_config instead
  chunking_config?: {
    chunk_size: number
    chunk_overlap: number
    embedding_model: string
  }
  is_active: boolean
  created_at: string
  updated_at: string
}

interface KnowledgeBaseListResponse {
  knowledge_bases: KnowledgeBase[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

// Demo knowledge bases for development
const DEMO_KNOWLEDGE_BASES: KnowledgeBase[] = [
  {
    id: 'demo-kb-1',
    name: 'Documentação Interna',
    description: 'Manuais, procedimentos e políticas da empresa',
    document_count: 45,
    chunk_count: 1230,
    embedding_model: 'text-embedding-3-small',
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'demo-kb-2',
    name: 'Base de Produtos',
    description: 'Catálogo completo de produtos e especificações',
    document_count: 120,
    chunk_count: 3450,
    embedding_model: 'text-embedding-3-small',
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'demo-kb-3',
    name: 'FAQ Atendimento',
    description: 'Perguntas frequentes e respostas padronizadas',
    document_count: 89,
    chunk_count: 890,
    embedding_model: 'text-embedding-3-small',
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'demo-kb-4',
    name: 'Artigos Técnicos',
    description: 'Documentação técnica e tutoriais',
    document_count: 67,
    chunk_count: 2100,
    embedding_model: 'text-embedding-3-small',
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
]

export function StepKnowledge() {
  const { t } = useTranslation('agents')
  const { watch, setValue } = useFormContext<AgentWizardData>()
  const selectedProject = useProjectStore((state) => state.selectedProject)

  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const debouncedSearch = useDebounce(searchQuery, 300)

  // Ensure selectedKBIds is always an array (handles undefined from form state)
  const watchedKBIds = watch('knowledge_base_ids')
  const selectedKBIds = Array.isArray(watchedKBIds) ? watchedKBIds : []

  // Load knowledge bases on mount
  useEffect(() => {
    async function loadKnowledgeBases() {
      if (!selectedProject?.id) {
        setKnowledgeBases(DEMO_KNOWLEDGE_BASES)
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const response = await api.get<KnowledgeBaseListResponse>(
          `/projects/${selectedProject.id}/knowledge-bases`,
          { params: { page_size: 100 } }
        )
        // Ensure we always set an array, even if API returns undefined
        const kbList = response.data?.knowledge_bases
        setKnowledgeBases(Array.isArray(kbList) ? kbList : DEMO_KNOWLEDGE_BASES)
      } catch {
        // Fallback to demo data on error
        setKnowledgeBases(DEMO_KNOWLEDGE_BASES)
        setError(t('wizard.knowledge.serverError'))
      } finally {
        setIsLoading(false)
      }
    }

    loadKnowledgeBases()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id])

  // Filter knowledge bases based on search (ensure array is never undefined)
  const safeKnowledgeBases = Array.isArray(knowledgeBases) ? knowledgeBases : []
  const filteredKBs = debouncedSearch
    ? safeKnowledgeBases.filter(
        (kb) =>
          kb.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          kb.description?.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    : safeKnowledgeBases

  const toggleKB = (kbId: string) => {
    const newSelection = selectedKBIds.includes(kbId)
      ? selectedKBIds.filter((id) => id !== kbId)
      : [...selectedKBIds, kbId]
    setValue('knowledge_base_ids', newSelection)
  }

  const isSelected = (kbId: string) => selectedKBIds.includes(kbId)

  const formatNumber = (num: number | undefined) => {
    if (num === undefined || num === null) return '0'
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`
    }
    return num.toString()
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-gray-400">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-600 border-t-emerald-500" />
          <span>{t('wizard.knowledge.loading')}</span>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="p-4 rounded-xl bg-white/5 border border-gray-700/50 animate-pulse"
            >
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-gray-700/50" />
                <div className="flex-1">
                  <div className="h-4 w-32 bg-gray-700/50 rounded mb-2" />
                  <div className="h-3 w-48 bg-gray-700/30 rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Label className="text-gray-300 flex items-center gap-2">
          <Brain className="h-4 w-4" />
          {t('wizard.knowledge.title')}
        </Label>
        <span className="text-sm text-gray-500">
          {t('wizard.knowledge.selected', { count: selectedKBIds.length })}
        </span>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
        <Input
          type="text"
          placeholder={t('wizard.knowledge.search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white/5 border-gray-700/50 text-white placeholder:text-gray-500"
        />
      </div>

      {/* Knowledge Bases List */}
      {filteredKBs.length === 0 ? (
        <div className="text-center py-12">
          <Brain className="h-12 w-12 mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">
            {searchQuery
              ? t('wizard.knowledge.noKnowledgeFound')
              : t('wizard.knowledge.noKnowledgeAvailable')}
          </p>
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="mt-2 text-sm text-emerald-400 hover:text-emerald-300"
            >
              {t('wizard.knowledge.clearSearch')}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredKBs.map((kb) => (
            <button
              key={kb.id}
              type="button"
              onClick={() => toggleKB(kb.id)}
              disabled={!kb.is_active}
              className={cn(
                'w-full p-4 rounded-xl text-left transition-all duration-200',
                'bg-white/5 border',
                isSelected(kb.id)
                  ? 'border-emerald-500 bg-emerald-500/10'
                  : 'border-gray-700/50 hover:border-gray-600 hover:bg-white/10',
                !kb.is_active && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div className="flex items-start gap-4">
                <div
                  className={cn(
                    'flex h-12 w-12 items-center justify-center rounded-xl',
                    isSelected(kb.id)
                      ? 'bg-emerald-500/30 text-emerald-400'
                      : 'bg-gray-700/50 text-gray-400'
                  )}
                >
                  <Brain className="h-6 w-6" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-white truncate">{kb.name}</p>
                    {isSelected(kb.id) && (
                      <Check className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                    )}
                    {!kb.is_active && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-400">
                        {t('wizard.knowledge.inactive')}
                      </span>
                    )}
                  </div>
                  {kb.description && (
                    <p className="text-sm text-gray-400 line-clamp-1 mb-2">
                      {kb.description}
                    </p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      {formatNumber(kb.document_count)} {t('wizard.knowledge.docs')}
                    </span>
                    {(kb.chunk_count !== undefined || kb.chunking_config) && (
                      <span className="flex items-center gap-1">
                        <Database className="h-3 w-3" />
                        {kb.chunk_count !== undefined
                          ? `${formatNumber(kb.chunk_count)} ${t('wizard.knowledge.chunks')}`
                          : `${kb.chunking_config?.chunk_size || 1000} ${t('wizard.knowledge.charsPerChunk')}`}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Info */}
      <p className="text-xs text-gray-500">
        {t('wizard.knowledge.knowledgeInfo')}
      </p>
    </div>
  )
}
